import { useEffect, useRef } from 'react';

// Lagen op de telefoon (vellen, een open mail of gesprek) krijgen elk een eigen stap in de
// geschiedenis van de browser. De terugbeweging van iOS (of de terugknop van Android) sluit dan de
// bovenste laag in plaats van de app te verlaten. In `history.state.layer` staat het nummer van de
// bovenste laag (0 of niets: geen laag). Sluit een laag op een andere manier (knop, Esc, unmount),
// dan gaat de geschiedenis één stap terug; een laag die vlak daarna opent wacht met haar eigen stap
// tot die teruggang verwerkt is, anders zou de teruggang de nieuwe stap meteen weer opeten.

interface Layer {
  id: number;
  close: () => void;
  /** Heeft deze laag al een stap in de geschiedenis? (Niet zolang een teruggang nog onderweg is.) */
  pushed: boolean;
}

let stack: Layer[] = [];
let seq = 0;
let installed = false;
/** Aanroepen van history.back() waarvan de popstate nog moet komen. */
let pendingBack = 0;

function level(): number {
  const s = history.state as { layer?: unknown } | null;
  return typeof s?.layer === 'number' ? s.layer : 0;
}

function pushPending(): void {
  for (const l of stack) {
    if (l.pushed) continue;
    l.pushed = true;
    history.pushState({ layer: l.id }, '');
  }
}

function onPop(): void {
  const at = level();
  if (pendingBack > 0) pendingBack -= 1;
  // Alles boven de stap waar de geschiedenis nu staat sluit, bovenste eerst.
  const closing = stack.filter((l) => l.pushed && l.id > at).sort((a, b) => b.id - a.id);
  stack = stack.filter((l) => !closing.includes(l));
  for (const l of closing) l.close();
  if (pendingBack > 0) return;
  if (stack.some((l) => !l.pushed)) pushPending();
  else if (at > 0 && !stack.some((l) => l.pushed)) {
    // Geland op de stap van een laag die al dicht is (bijvoorbeeld vooruit genavigeerd): verder terug.
    pendingBack += 1;
    history.back();
  }
}

function install(): void {
  if (installed) return;
  installed = true;
  window.addEventListener('popstate', onPop);
  // De pagina kan geladen zijn op de stap van een eerdere laag: die stap geldt dan als basis.
  if (level() !== 0) history.replaceState({ ...((history.state as object | null) ?? {}), layer: 0 }, '');
}

/** Meldt een open laag aan; de teruggegeven functie meldt hem af (sluiten via knop, Esc of unmount). */
export function pushLayer(close: () => void): () => void {
  install();
  seq += 1;
  const layer: Layer = { id: seq, close, pushed: false };
  stack.push(layer);
  if (pendingBack === 0) pushPending();
  return () => {
    const idx = stack.indexOf(layer);
    if (idx < 0) return; // al gesloten via de terugknop
    stack.splice(idx, 1);
    if (layer.pushed && level() === layer.id) {
      pendingBack += 1;
      history.back();
    }
  };
}

/** Zolang `open` waar is heeft deze laag een stap in de geschiedenis; de terugknop roept `onClose` aan. */
export function useLayer(open: boolean, onClose: () => void): void {
  const ref = useRef(onClose);
  useEffect(() => {
    ref.current = onClose;
  });
  useEffect(() => {
    if (!open) return;
    return pushLayer(() => ref.current());
  }, [open]);
}

/** Alleen voor tests: stapel en luisteraar weg. */
export function resetLayersForTests(): void {
  if (installed) window.removeEventListener('popstate', onPop);
  installed = false;
  stack = [];
  pendingBack = 0;
}
